import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';

/**
 * Marketplace subscription FAQ — shared accordion shown on the My Subscriptions
 * and (authenticated) Marketplace pages. Answers reflect the real engine
 * behavior for pause / unsubscribe / not-logged-in with respect to open
 * positions, plus the daily-login routine. Single source of truth for this copy.
 */

interface FaqItem {
  q: string;
  a: React.ReactNode;
}

const FAQS: FaqItem[] = [
  {
    q: 'How do I subscribe to a strategy?',
    a: (
      <>Open <span className="font-medium">MarketPlace</span> (next to Dashboard), open a basket to see its
      details &amp; backtest, then click <span className="font-medium">Subscribe</span> and choose your trading
      account and lot multiplier.</>
    ),
  },
  {
    q: 'What do I need to do every trading day?',
    a: (
      <>Log in to the platform <span className="font-medium">by 9:10 AM IST</span> so your broker session is active
      before the market opens. Keep your broker account linked with <span className="font-medium">sufficient
      capital</span>. Subscribed strategies then run automatically between <span className="font-medium">9:16 AM and
      3:39 PM</span>, Monday to Friday — no manual action needed during the day.</>
    ),
  },
  {
    q: 'What happens if I pause a subscription?',
    a: (
      <><span className="font-medium">New trades stop immediately.</span> Your existing open positions are
      <span className="font-medium"> not closed</span> — they continue to be managed (stop-loss, target, scheduled
      exit and end-of-day square-off) and will exit normally. Resume anytime to start taking new trades again.</>
    ),
  },
  {
    q: 'What happens if I unsubscribe?',
    a: (
      <>Unsubscribing stops all new trades and removes the strategy from your account. It does
      <span className="font-medium"> not automatically close your open positions.</span> We recommend you square off
      (close) any open positions — or simply <span className="font-medium">Pause</span> instead — before
      unsubscribing. If you unsubscribe while a position is open, it stays protected by its resting stop-loss and the
      end-of-day square-off, but its scheduled exit may not run.</>
    ),
  },
  {
    q: "What happens if I don't log in on a trading day?",
    a: (
      <>If your broker session isn&rsquo;t active <span className="font-medium">by 9:16 AM</span>, that account is
      <span className="font-medium"> skipped for the whole day</span> — no new trades, and platform monitoring
      (stop-loss re-placement, target, scheduled and end-of-day exits) won&rsquo;t run. A stop-loss already placed at
      the broker still protects you, but to stay fully managed you <span className="font-medium">must log in daily by
      9:10 AM</span>. Otherwise an intraday position can carry overnight unintentionally.</>
    ),
  },
  {
    q: 'Pause or Unsubscribe — which should I use?',
    a: (
      <>Use <span className="font-medium">Pause</span> to stop new trades temporarily while keeping your existing
      positions managed. Use <span className="font-medium">Unsubscribe</span> only when you&rsquo;re flat (no open
      positions) and want to remove the strategy entirely.</>
    ),
  },
  {
    q: 'Can I change my lot size after subscribing?',
    a: (
      <>Yes — edit the lot multiplier on the subscription card under <span className="font-medium">My
      Subscriptions</span>.</>
    ),
  },
  {
    q: 'How much capital do I need?',
    a: (
      <>Each basket shows the capital required per lot. Your total is capital-per-lot × your lot multiplier. Keep
      enough funds in your trading account to cover it.</>
    ),
  },
  {
    q: 'Can I build my own strategies instead?',
    a: (
      <>Yes — use the <span className="font-medium">Strategy Builder</span> on the Baskets page to create your own
      baskets and multi-leg option strategies, fully automated.</>
    ),
  },
];

const MarketplaceFAQ: React.FC<{ className?: string }> = ({ className }) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-gray-400" />
            Frequently Asked Questions
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {FAQS.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={item.q} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left"
                aria-expanded={open}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white">{item.q}</span>
                <ChevronDown
                  className={cn('h-4 w-4 flex-shrink-0 text-gray-400 transition-transform', open && 'rotate-180')}
                />
              </button>
              {open && (
                <div className="px-6 pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MarketplaceFAQ;
